import React from 'react';
import {
    Box,
    Stack,
    TextField,
    Button,
    Text,
    Card,
    Divider,
    Actions,
    Inline
} from 'braid-design-system';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from 'react-i18next';
import {
    getCustomerTypeInfoFields,
    getCustomerTypeDocs,
    createCustomerInfo,
    uploadCustomerDocument,
    getCustomerInfo,
    getCustomerDocuments,
    getCustomerNominee,
    upsertCustomerNominee
} from '../../services/approveMobileCustomerService';
import DateField from '../DateField';
import { config } from '../../config/app.config';
import Loading from "../../components/Loading";

interface CustomerDocsInfoFormProps {
    customerId: number;
    customerTypeId: number;
    onSuccess?: () => void;
    onCancel?: () => void;
}

interface InfoField {
    id: number;
    field_name: string;
    ci_customer_type_id: number;
    required: number;
}

interface DocField {
    id: number;
    doc_name: string;
    ci_customer_type_id: number;
    required: number;
}

const CustomerDocsInfoForm: React.FC<CustomerDocsInfoFormProps> = ({
    customerId,
    customerTypeId,
    onSuccess,
    onCancel
}) => {
    const toast = useToast();
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [fetchingData, setFetchingData] = React.useState(true);

    const [infoFields, setInfoFields] = React.useState<InfoField[]>([]);
    const [docFields, setDocFields] = React.useState<DocField[]>([]);
    const [infoValues, setInfoValues] = React.useState<Record<number, string>>({});
    const [docFiles, setDocFiles] = React.useState<Record<number, File | null>>({});
    const [existingDocs, setExistingDocs] = React.useState<Record<number, string>>({});
    const [nomineeValues, setNomineeValues] = React.useState({
        name_ln1: '',
        name_ln2: '',
        name_ln3: '',
        relationship: '',
        reg_id_num: '',
        date_of_birth: ''
    });

    const isImage = (file: string | File) => {
        const name = typeof file === 'string' ? file : file.name;
        return /\.(jpg|jpeg|png)$/i.test(name);
    };

    const isPDF = (file: string | File) => {
        const name = typeof file === 'string' ? file : file.name;
        return /\.pdf$/i.test(name);
    };

    // Fetch required fields and documents
    React.useEffect(() => {
        const loadData = async () => {
            try {
                setFetchingData(true);
                const [fields, docs, infoData, docData, nomineeData] = await Promise.all([
                    getCustomerTypeInfoFields(customerTypeId),
                    getCustomerTypeDocs(customerTypeId),
                    getCustomerInfo(customerId),
                    getCustomerDocuments(customerId),
                    getCustomerNominee(customerId)
                ]);

                setInfoFields(fields);
                setDocFields(docs);

                // Initialize empty values
                const initialInfoValues: Record<number, string> = {};
                fields.forEach((field: InfoField) => {
                    initialInfoValues[field.id] = '';
                });
                setInfoValues(initialInfoValues);

                const initialDocFiles: Record<number, File | null> = {};
                docs.forEach((doc: DocField) => {
                    initialDocFiles[doc.id] = null;
                });
                setDocFiles(initialDocFiles);

                const existingInfoMap: Record<number, string> = {};
                infoData.forEach((item: any) => {
                    existingInfoMap[item.ci_customer_type_info_id] = item.value;
                });
                setInfoValues(existingInfoMap);

                const existingDocMap: Record<number, string> = {};
                docData.forEach((item: any) => {
                    if (item.file_url) {
                        let url = item.file_url;
                        // If it's already a full URL (http/https), use as-is
                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                            // If path already contains /api/customer/files/, don't double-prepend
                            if (url.includes('/api/customer/files/')) {
                                const base = config.apiUrl.replace(/\/api\/?$/, '');
                                const parts = url.split('/api/customer/files/');
                                if (parts.length === 2) {
                                    url = `${base}/api/customer/files/${parts[1].replace(/^\//, '')}`;
                                }
                            } else {
                                // Relative path - prepend the API base URL
                                const cleanPath = url.replace(/^uploads\//, '').replace(/^\//, '');
                                url = `${config.apiUrl.replace(/\/api\/?$/, '')}/api/customer/files/${cleanPath}`;
                            }
                        }
                        // Clean up any double slashes in URL path (ignoring http:// or https://)
                        url = url.replace(/([^:]\/)\/+/g, '$1');
                        existingDocMap[item.ci_customer_type_doc_id] = url;
                    } else {
                        existingDocMap[item.ci_customer_type_doc_id] = '';
                    }
                });
                setExistingDocs(existingDocMap);

                if (nomineeData) {
                    setNomineeValues({
                        name_ln1: nomineeData.name_ln1 || '',
                        name_ln2: nomineeData.name_ln2 || '',
                        name_ln3: nomineeData.name_ln3 || '',
                        relationship: nomineeData.relationship || '',
                        reg_id_num: nomineeData.reg_id_num || '',
                        date_of_birth: nomineeData.date_of_birth ? nomineeData.date_of_birth.slice(0, 10) : '',
                    });
                }


            } catch (error: any) {
                toast.showError(error.message || t('page.approve_mobile_app_customer.docs_info.error.load_failed', { defaultValue: 'Failed to load required fields and documents' }));
            } finally {
                setFetchingData(false);
            }
        };

        loadData();
    }, [customerId, customerTypeId]);

    const handleInfoValueChange = (fieldId: number, value: string) => {
        setInfoValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const handleDocFileChange = (docId: number, file: File | null) => {
        setDocFiles(prev => ({
            ...prev,
            [docId]: file
        }));
    };

    const handleNomineeChange = (field: keyof typeof nomineeValues, value: string) => {
        setNomineeValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all info fields are filled
        for (const field of infoFields) {
            if (field.required === 1 && (!infoValues[field.id] || !infoValues[field.id].trim())) {
                toast.showError(
                    t('page.approve_mobile_app_customer.docs_info.validation.info_required', {
                        defaultValue: '{{field}} is required',
                        field: field.field_name
                    })
                );
                return;
            }
        }

        // Validate all documents are uploaded
        for (const doc of docFields) {
            if (doc.required === 1 && (!docFiles[doc.id] && !existingDocs[doc.id])) {
                toast.showError(
                    t('page.approve_mobile_app_customer.docs_info.validation.doc_required', {
                        defaultValue: '{{doc}} is required',
                        doc: doc.doc_name
                    })
                );
                return;
            }
        }

        // if (!nomineeValues.name_ln1.trim()) {
        //     toast.showError(
        //         t('page.approve_mobile_app_customer.docs_info.validation.nominee_name_required', {
        //             defaultValue: 'Nominee name is required'
        //         })
        //     );
        //     return;
        // }

        // if (!nomineeValues.relationship.trim()) {
        //     toast.showError(
        //         t('page.approve_mobile_app_customer.docs_info.validation.nominee_relation_required', {
        //             defaultValue: 'Nominee relation is required'
        //         })
        //     );
        //     return;
        // }

        // if (!nomineeValues.reg_id_num.trim()) {
        //     toast.showError(
        //         t('page.approve_mobile_app_customer.docs_info.validation.nominee_nic_required', {
        //             defaultValue: 'Nominee NIC is required'
        //         })
        //     );
        //     return;
        // }

        // if (!nomineeValues.date_of_birth.trim()) {
        //     toast.showError(
        //         t('page.approve_mobile_app_customer.docs_info.validation.nominee_dob_required', {
        //             defaultValue: 'Nominee date of birth is required'
        //         })
        //     );
        //     return;
        // }

        try {
            setLoading(true);

            if (nomineeValues.name_ln1.trim()) {
                await upsertCustomerNominee(customerId, nomineeValues);
            }

            // Save all info fields
            for (const field of infoFields) {
                await createCustomerInfo(customerId, {
                    ci_customer_type_info_id: field.id,
                    file: infoValues[field.id]
                });
            }

            // Upload all documents
            for (const doc of docFields) {
                const file = docFiles[doc.id];
                if (file) {
                    await uploadCustomerDocument(customerId, {
                        ci_customer_type_doc_id: doc.id,
                        file: file
                    });
                }
            }

            toast.showSuccess(
                t('page.approve_mobile_app_customer.docs_info.success.saved', {
                    defaultValue: 'Customer information and documents saved successfully'
                })
            );

            if (onSuccess) {
                onSuccess();
                window.location.reload();
            }
        } catch (error: any) {
            toast.showError(
                error.message ||
                t('page.approve_mobile_app_customer.docs_info.error.save_failed', {
                    defaultValue: 'Failed to save customer information'
                })
            );
        } finally {
            setLoading(false);
        }
    };

    if (fetchingData) {
        return (
            <Box padding="medium">
                <Stack space="large" align="center">
                    <Text>
                        {t('page.approve_mobile_app_customer.docs_info.loading', {
                            defaultValue: 'Loading required fields and documents...'
                        })}
                    </Text>
                </Stack>
            </Box>
        );
    }

    if (loading) {
        return (
            <Loading message={t('page.approve_mobile_app_customer.form.updating_customer_info_docs', { defaultValue: 'Updating customer documents and information...' })} />
        );
    }

    return (
        <Box padding="medium">
            <form onSubmit={handleSubmit} autoComplete="off">
                <Stack space="large">
                    {/* Required Info Fields Section */}
                    {infoFields.length > 0 && (
                        <>
                            <Text weight="strong" size="large">
                                {t('page.approve_mobile_app_customer.docs_info.section.info', { defaultValue: 'Required Information' })}
                            </Text>
                            <Divider />
                            <Stack space="medium">
                                {infoFields.map((field) => (
                                    <TextField
                                        key={field.id}
                                        label={
                                            <span>
                                                {field.field_name}
                                                {field.required === 1 && <span> *</span>}
                                            </span>
                                        }
                                        value={infoValues[field.id] || ''}
                                        onChange={(e) => handleInfoValueChange(field.id, e.currentTarget.value)}
                                        placeholder={t('page.approve_mobile_app_customer.docs_info.placeholder.info', {
                                            defaultValue: 'Enter {{field}}',
                                            field: field.field_name
                                        })}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}

                    {/* Nominee Details Section */}
                    <Text weight="strong" size="large">
                        {t('page.approve_mobile_app_customer.docs_info.section.nominee', { defaultValue: 'Nominee Details' })}
                    </Text>
                    <Divider />
                    <Card>
                        <Stack space="medium">
                            <TextField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.name_ln1', { defaultValue: 'Name (English)' })}
                                value={nomineeValues.name_ln1}
                                onChange={(e) => handleNomineeChange('name_ln1', e.currentTarget.value)}
                            />
                            <TextField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.name_ln2', { defaultValue: 'Name (Sinhala)' })}
                                value={nomineeValues.name_ln2}
                                onChange={(e) => handleNomineeChange('name_ln2', e.currentTarget.value)}
                            />
                            <TextField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.name_ln3', { defaultValue: 'Name (Tamil)' })}
                                value={nomineeValues.name_ln3}
                                onChange={(e) => handleNomineeChange('name_ln3', e.currentTarget.value)}
                            />
                            <TextField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.relationship', { defaultValue: 'Relation' })}
                                value={nomineeValues.relationship}
                                onChange={(e) => handleNomineeChange('relationship', e.currentTarget.value)}
                            />
                            <TextField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.nic', { defaultValue: 'NIC' })}
                                value={nomineeValues.reg_id_num}
                                onChange={(e) => handleNomineeChange('reg_id_num', e.currentTarget.value)}
                            />
                            <DateField
                                label={t('page.approve_mobile_app_customer.docs_info.nominee.dob', { defaultValue: 'Date of Birth' })}
                                onlyForToday={true}
                                onChange={(value) => handleNomineeChange('date_of_birth', value)}
                                value={nomineeValues.date_of_birth ? nomineeValues.date_of_birth.slice(0, 10) : ''}
                                placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                            />
                        </Stack>
                    </Card>

                    <Divider />

                    {/* Required Documents Section */}
                    {docFields.length > 0 && (
                        <>
                            <Text weight="strong" size="large">
                                {t('page.approve_mobile_app_customer.docs_info.section.docs', { defaultValue: 'Required Documents' })}
                            </Text>
                            <Divider />
                            <Stack space="medium">
                                {docFields.map((doc) => (
                                    <Card key={doc.id}>
                                        <Stack space="small">
                                            <Text weight="strong" size="small">
                                                {doc.doc_name}{doc.required === 1 ? ' *' : ''}
                                            </Text>

                                            {existingDocs[doc.id] && !docFiles[doc.id] ? (
                                                <Stack space="xsmall">
                                                    <Text size="small" tone="positive">
                                                        Existing file available
                                                    </Text>

                                                    {/* IMAGE PREVIEW */}
                                                    {isImage(existingDocs[doc.id]) ? (
                                                        <img
                                                            src={existingDocs[doc.id]}
                                                            alt="document"
                                                            style={{ maxWidth: '200px', borderRadius: '8px' }}
                                                        />
                                                    ) : null}

                                                    {/* PDF VIEW */}
                                                    {isPDF(existingDocs[doc.id]) ? (
                                                        <iframe
                                                            src={existingDocs[doc.id]}
                                                            title="Document Preview"
                                                            style={{ width: '100%', height: '200px' }}
                                                        />
                                                    ) : null}

                                                    {/* OPEN BUTTON */}
                                                    <Inline space="small" align="left">
                                                        <Button
                                                            variant="ghost"
                                                            onClick={() => window.open(existingDocs[doc.id], '_blank')}
                                                        >
                                                            View File
                                                        </Button>
                                                    </Inline>
                                                </Stack>
                                            ) : null}

                                            <input
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] || null;
                                                    handleDocFileChange(doc.id, file);
                                                }}
                                            />

                                            {docFiles[doc.id] && (
                                                <Text size="small" tone="secondary">
                                                    New: {docFiles[doc.id]?.name}
                                                </Text>
                                            )}
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        </>
                    )}

                    <Divider />

                    {/* Action Buttons */}
                    <Actions>
                        <Button
                            type="submit"
                            tone="brandAccent"
                            loading={loading}
                        >
                            {t('page.approve_mobile_app_customer.docs_info.button.save', { defaultValue: 'Save' })}
                        </Button>
                        {onCancel && (
                            <Button
                                variant="ghost"
                                onClick={onCancel}
                            >
                                {t('page.approve_mobile_app_customer.docs_info.button.cancel', { defaultValue: 'Cancel' })}
                            </Button>
                        )}
                    </Actions>
                </Stack>
            </form>
        </Box>
    );
};

export default CustomerDocsInfoForm;
